import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { IS_FACTURACION } from '../config';
import {
  clearCanastaDraft,
  enqueueBodegaMutation,
  isNetworkError,
  loadCanastaDraft,
  saveCanastaDraft,
} from '../utils/offlineBodegaQueue';

export function itemPickKey(item, index) {
  return String(item.id ?? `${item.codigo}-${index}`);
}

function parseKeyArray(val) {
  if (Array.isArray(val)) return val.map(String);
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function keysSig(picked, missing) {
  return `${[...picked].map(String).sort().join(',')}|${[...missing].map(String).sort().join(',')}`;
}

export default function usePickList(pedidoId, items = [], pin, separadorNombre = null, { readOnly = false } = {}) {
  const keys = useMemo(
    () => items.map((it, idx) => itemPickKey(it, idx)),
    [items]
  );

  const [picked, setPicked] = useState(new Set());
  const [missing, setMissing] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedOffline, setSavedOffline] = useState(false);
  const saveTimer = useRef(null);
  const revisionRef = useRef(0);
  const pickedRef = useRef(picked);
  const missingRef = useRef(missing);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  pickedRef.current = picked;
  missingRef.current = missing;

  useEffect(() => {
    if (!pedidoId) return undefined;
    let cancelled = false;
    setLoaded(false);
    setSavedOffline(false);
    dirtyRef.current = false;
    (async () => {
      const draft = loadCanastaDraft(pedidoId);
      try {
        const res = await api.getPedido(pedidoId);
        const serverKeys = parseKeyArray(
          IS_FACTURACION ? res.data?.factura_pick_keys : res.data?.pick_keys
        );
        const serverMissing = parseKeyArray(
          IS_FACTURACION ? res.data?.factura_missing_keys : res.data?.missing_keys
        );
        const serverRevision = Number(
          (IS_FACTURACION ? res.data?.factura_revision : res.data?.canasta_revision) || 0
        );
        if (cancelled) return;
        revisionRef.current = serverRevision;
        if (draft?.keys?.length || draft?.missing_keys?.length) {
          const draftMissing = new Set((draft.missing_keys || []).map(String));
          setMissing(draftMissing);
          setPicked(new Set((draft.keys || []).map(String).filter((k) => !draftMissing.has(k))));
          if (draft.revision != null) revisionRef.current = Math.max(serverRevision, Number(draft.revision));
          setSavedOffline(true);
        } else {
          const miss = new Set(serverMissing);
          setMissing(miss);
          setPicked(new Set(serverKeys.filter((k) => !miss.has(k))));
        }
      } catch {
        if (cancelled) return;
        if (draft?.keys || draft?.missing_keys) {
          const draftMissing = new Set((draft.missing_keys || []).map(String));
          setMissing(draftMissing);
          setPicked(new Set((draft.keys || []).map(String).filter((k) => !draftMissing.has(k))));
          if (draft.revision != null) revisionRef.current = Number(draft.revision);
          setSavedOffline(true);
        } else {
          setPicked(new Set());
          setMissing(new Set());
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pedidoId, pin]);

  const persist = useCallback(async () => {
    if (!pedidoId || !pin || readOnly) return;
    if (savingRef.current) {
      pendingSaveRef.current = true;
      return;
    }
    savingRef.current = true;
    const keysArr = [...pickedRef.current];
    const missingArr = [...missingRef.current];
    const sentSig = keysSig(keysArr, missingArr);
    const revision = revisionRef.current;
    saveCanastaDraft(pedidoId, keysArr, revision, missingArr);

    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        enqueueBodegaMutation({
          type: 'canasta',
          pedidoId,
          keys: keysArr,
          missing_keys: missingArr,
          separadorNombre: separadorNombre || null,
          revision,
        });
        setSavedOffline(true);
        setSaveError('');
        return;
      }

      const send = (rev) => api.saveCanasta(pedidoId, keysArr, separadorNombre, rev, missingArr);

      let res;
      try {
        res = await send(revision);
      } catch (err) {
        const msg = String(err?.message || '');
        const staleRev = err?.status === 409 && /desactualizada/i.test(msg);
        if (!staleRev) throw err;
        try {
          const fresh = await api.getPedido(pedidoId);
          revisionRef.current = Number(
            (IS_FACTURACION ? fresh.data?.factura_revision : fresh.data?.canasta_revision) || 0
          );
        } catch {
          revisionRef.current = revision + 1;
        }
        // Reenvía LO QUE ESTÁ MARCADO en pantalla. Nunca pisa la UI con el servidor.
        res = await api.saveCanasta(
          pedidoId,
          [...pickedRef.current],
          separadorNombre,
          revisionRef.current,
          [...missingRef.current]
        );
      }

      if (res?.data?.factura_revision != null) {
        revisionRef.current = Number(res.data.factura_revision);
      } else if (res?.data?.canasta_revision != null) {
        revisionRef.current = Number(res.data.canasta_revision);
      } else {
        revisionRef.current = revision + 1;
      }

      if (keysSig(pickedRef.current, missingRef.current) === sentSig) {
        clearCanastaDraft(pedidoId);
      } else {
        saveCanastaDraft(pedidoId, [...pickedRef.current], revisionRef.current, [...missingRef.current]);
      }
      setSavedOffline(false);
      setSaveError('');
    } catch (err) {
      if (err?.status === 409) {
        // Otro error 409 (separador / estado): no borrar marcas de la pantalla.
        saveCanastaDraft(pedidoId, [...pickedRef.current], revisionRef.current, [...missingRef.current]);
        setSaveError(err.message || 'No se pudo guardar la canasta; las marcas siguen en la tablet.');
        return;
      }
      enqueueBodegaMutation({
        type: 'canasta',
        pedidoId,
        keys: [...pickedRef.current],
        missing_keys: [...missingRef.current],
        separadorNombre: separadorNombre || null,
        revision: revisionRef.current,
      });
      setSavedOffline(true);
      setSaveError(
        isNetworkError(err)
          ? 'Sin internet — canasta guardada en la tablet. Se enviará al volver la señal.'
          : err.message || 'No se guardó en el servidor; quedó en la tablet.'
      );
    } finally {
      savingRef.current = false;
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        persist();
      }
    }
  }, [pedidoId, pin, readOnly, separadorNombre]);

  useEffect(() => {
    if (!pedidoId || !loaded || !pin || readOnly || !dirtyRef.current) return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persist();
    }, 280);
    return () => clearTimeout(saveTimer.current);
  }, [picked, missing, pedidoId, pin, loaded, persist, readOnly]);

  const toggle = useCallback(
    (key) => {
      if (readOnly) return;
      dirtyRef.current = true;
      setMissing((prevMiss) => {
        const nextMiss = new Set(prevMiss);
        nextMiss.delete(key);
        return nextMiss;
      });
      setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [readOnly]
  );

  const markMissing = useCallback(
    (key) => {
      if (readOnly) return;
      dirtyRef.current = true;
      setPicked((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setMissing((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [readOnly]
  );

  const donePicked = keys.filter((k) => picked.has(k)).length;
  const doneMissing = keys.filter((k) => missing.has(k)).length;
  const done = donePicked + doneMissing;
  const total = keys.length;
  const allDone = total > 0 && done === total;
  const progress = total ? Math.round((done / total) * 100) : 0;

  return {
    picked,
    missing,
    toggle,
    markMissing,
    done,
    donePicked,
    doneMissing,
    total,
    allDone,
    progress,
    saveError,
    savedOffline,
    loaded,
  };
}
